import {
  APP_ROUTES_BY_ID,
  AppCrumbRoute,
  AppPageRoute,
  formatPathSegmentLabel,
  matchAppRoute,
  resolveRouteHref,
  resolveRouteLabel,
} from "navigation/route-registry";

export interface BreadcrumbChild {
  href: string;
  label: string;
}

export interface BreadcrumbItem {
  children?: BreadcrumbChild[];
  href: string;
  isCurrent: boolean;
  label: string;
}

const DASHBOARD_CRUMB: BreadcrumbItem = {
  href: "/dashboard",
  isCurrent: true,
  label: "Dashboard",
};

const cleanPathname = (pathname: string) => pathname.split("?")[0].split("#")[0] || "/";

const fallbackBreadcrumbs = (cleanedPath: string): BreadcrumbItem[] => {
  const segments = cleanedPath.split("/").filter(Boolean);

  if (segments.length === 0 || cleanedPath === "/dashboard") {
    return [{ ...DASHBOARD_CRUMB }];
  }

  return [
    {
      href: cleanedPath,
      isCurrent: true,
      label: formatPathSegmentLabel(segments[segments.length - 1]),
    },
  ];
};

const walkParentTrail = (route: AppPageRoute | AppCrumbRoute, params: Record<string, string>) => {
  const trail: BreadcrumbItem[] = [];
  const seen = new Set<string>();
  let current: AppPageRoute | AppCrumbRoute | undefined = route;

  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    trail.unshift({
      href: resolveRouteHref(current, params),
      isCurrent: false,
      label: resolveRouteLabel(current, params),
    });

    const parent = current.parentId ? APP_ROUTES_BY_ID[current.parentId] : undefined;

    if (!parent || parent.kind === "redirect" || parent.id === "dashboard") {
      break;
    }

    current = parent;
  }

  if (trail.length) {
    trail[trail.length - 1].isCurrent = true;
  }

  return trail;
};

export const getBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  const cleanedPath = cleanPathname(pathname);

  if (cleanedPath === "/dashboard" || cleanedPath === "/") {
    return [{ ...DASHBOARD_CRUMB }];
  }

  const matched = matchAppRoute(cleanedPath);

  if (!matched) {
    return fallbackBreadcrumbs(cleanedPath);
  }

  if (matched.route.kind === "redirect") {
    return getBreadcrumbs(matched.route.pathname);
  }

  const trail = walkParentTrail(matched.route, matched.params);

  return trail.length ? trail : fallbackBreadcrumbs(cleanedPath);
};

export const applyCurrentCrumbLabel = (
  items: BreadcrumbItem[],
  label?: string | null,
): BreadcrumbItem[] => {
  const nextLabel = label?.trim();

  if (!nextLabel) {
    return items;
  }

  return items.map((item) => (item.isCurrent ? { ...item, label: nextLabel } : item));
};
